import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import GiftCardCodeInput from '../components/GiftCardCodeInput';
import { useAuth } from '../hooks/useAuth';
import { giftCardApi } from '../services/giftCardApi';

// A fully entered code: YOYO- plus three 4-char groups.
const COMPLETE_CODE = /^YOYO-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/;

export default function GiftCardRedeem() {
  const { t } = useTranslation('giftCard');
  const { isAuthenticated, loading: authLoading, getToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenParam = searchParams.get('token') || undefined;
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ amount: number; balance: number } | null>(null);
  const [preview, setPreview] = useState<{ faceValue: number; status: string } | null>(null);

  // Preserve the redemption target across a login redirect.
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      const here = `/gift-cards/redeem${tokenParam ? `?token=${encodeURIComponent(tokenParam)}` : ''}`;
      navigate('/login', { state: { from: here } });
    }
  }, [authLoading, isAuthenticated, navigate, tokenParam]);

  // Surface a friendly localized message for the common "not found" case;
  // fall back to whatever the server returned for anything else.
  const friendlyError = (err: unknown): string => {
    const msg = err instanceof Error ? err.message : '';
    if (/not found/i.test(msg)) return t('redeem.notFound');
    if (/already been redeemed/i.test(msg)) return t('redeem.alreadyRedeemed');
    if (/not available for redemption/i.test(msg)) return t('redeem.notAvailable');
    return msg || t('redeem.notAvailable');
  };

  // After login, load the card by token so we can show "$X ready" before redeeming.
  useEffect(() => {
    if (authLoading || !isAuthenticated || !tokenParam) return;
    giftCardApi
      .preview({ token: tokenParam })
      .then(setPreview)
      .catch((err) => setError(friendlyError(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, tokenParam, t]);

  const handleRedeem = async () => {
    setError(null);
    if (!tokenParam && !COMPLETE_CODE.test(code)) {
      setError(t('redeem.enterCode'));
      return;
    }
    try {
      setSubmitting(true);
      const token = await getToken();
      if (!token) throw new Error('Please sign in first');
      const result = await giftCardApi.redeem(
        tokenParam ? { token: tokenParam } : { code: code.trim() },
        token
      );
      setDone({ amount: result.redeemedAmount, balance: result.newBalance });
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (done) {
    return (
      <div className="max-w-md mx-auto px-4 pt-6 pb-16 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('redeem.doneTitle')}</h1>
        <p className="text-gray-600 mb-1">
          {t('redeem.doneAdded', { amount: (done.amount / 100).toFixed(2) })}
        </p>
        <p className="text-gray-900 font-semibold mb-6">
          {t('redeem.doneBalance', { balance: (done.balance / 100).toFixed(2) })}
        </p>
        <button
          onClick={() => navigate('/activities')}
          className="py-3 px-6 rounded-lg bg-primary text-white font-semibold"
        >
          {t('redeem.browse')}
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-4 pt-6 pb-16">
      <Helmet>
        <title>{t('meta.titleRedeem')}</title>
        <meta name="description" content={t('meta.descriptionRedeem')} />
      </Helmet>
      <div className="flex items-start justify-between gap-3 mb-2">
        <h1 className="text-2xl font-bold text-gray-900">{t('redeem.title')}</h1>
        <Link
          to="/gift-cards"
          className="mt-1 text-sm font-medium text-primary border border-primary rounded-full px-3 py-1.5 hover:bg-primary/5 whitespace-nowrap"
        >
          {t('redeem.buyLink')}
        </Link>
      </div>
      <p className="text-gray-600 mb-6">{t('redeem.subtitle')}</p>
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          {error}
        </div>
      )}
      {preview && preview.status === 'ACTIVE' && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4 text-center">
          <p className="text-sm text-gray-600">{t('redeem.valueLabel')}</p>
          <p className="text-3xl font-bold text-primary">${(preview.faceValue / 100).toFixed(2)}</p>
          <p className="text-sm text-gray-600 mt-1">{t('redeem.ready')}</p>
        </div>
      )}
      {preview && preview.status !== 'ACTIVE' && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800">
          {preview.status === 'REDEEMED' ? t('redeem.alreadyRedeemed') : t('redeem.notAvailable')}
        </div>
      )}
      {!tokenParam && (
        <div className="mb-4">
          <GiftCardCodeInput onChange={setCode} />
        </div>
      )}
      <button
        onClick={handleRedeem}
        disabled={submitting || (preview != null && preview.status !== 'ACTIVE')}
        className="w-full py-3 px-4 rounded-lg font-semibold bg-primary text-white hover:bg-primary-dark disabled:opacity-60"
      >
        {submitting ? t('redeem.submitting') : t('redeem.submit')}
      </button>
    </div>
  );
}
